import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import type { VisibilityState } from '@tanstack/react-table';
import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getColumnLabel,
  PINNED_COLUMN_IDS,
} from '@/pages/Vorgaenge/columns/columnMeta';
import { SortableColumnItem } from './SortableColumnItem';

export interface TableSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnOrder: string[];
  columnVisibility: VisibilityState;
  onColumnOrderChange: (next: string[]) => void;
  onColumnVisibilityChange: (next: VisibilityState) => void;
  onResetToDefault: () => void;
}

/**
 * Modal that lets the user show/hide and reorder the user-controllable
 * columns of the Vorgänge tables. Pinned columns (select, actions) appear as
 * disabled placeholders on the left of the strip; they cannot be reordered
 * or hidden.
 *
 * All changes apply live via `onColumnOrderChange` / `onColumnVisibilityChange`
 * so closing the dialog (or the page reloading) simply keeps whatever state
 * the user has produced. `onResetToDefault` reverts to the factory defaults.
 */
export const TableSettingsModal: React.FC<TableSettingsModalProps> = ({
  open,
  onOpenChange,
  columnOrder,
  columnVisibility,
  onColumnOrderChange,
  onColumnVisibilityChange,
  onResetToDefault,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = columnOrder.indexOf(String(active.id));
    const newIndex = columnOrder.indexOf(String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    onColumnOrderChange(arrayMove(columnOrder, oldIndex, newIndex));
  };

  const handleToggleVisibility = (id: string, visible: boolean) => {
    onColumnVisibilityChange({ ...columnVisibility, [id]: visible });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Breite nur hier: wächst mit Viewport, Kappe 80rem. sm: überschreibt Dialog-Default max-w-lg. */}
      <DialogContent
        showCloseButton={false}
        className="w-full max-w-[min(95vw,80rem)] sm:max-w-[min(95vw,80rem)]"
      >
        <DialogHeader>
          <DialogTitle>Spalten anpassen</DialogTitle>
          <DialogDescription>
            Spalten per Ziehen sortieren, Sichtbarkeit unten per Auge schalten.
          </DialogDescription>
        </DialogHeader>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <div className="flex max-w-full items-stretch gap-2 overflow-x-auto overflow-y-hidden pb-2">
            {PINNED_COLUMN_IDS.map((id) => (
              <SortableColumnItem
                key={id}
                id={id}
                label={getColumnLabel(id)}
                visible
                onToggleVisible={() => undefined}
                pinned
              />
            ))}
            <SortableContext
              items={columnOrder}
              strategy={horizontalListSortingStrategy}
            >
              {columnOrder.map((id) => (
                <SortableColumnItem
                  key={id}
                  id={id}
                  label={getColumnLabel(id)}
                  visible={columnVisibility[id] !== false}
                  onToggleVisible={(v) => handleToggleVisibility(id, v)}
                />
              ))}
            </SortableContext>
          </div>
        </DndContext>

        <DialogFooter className="sm:justify-between">
          <Button type="button" variant="outline" onClick={onResetToDefault}>
            Auf Standard zurücksetzen
          </Button>
          <DialogClose asChild>
            <Button type="button">Schließen</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
